import { useState, useEffect, useCallback } from 'react';
import './Observability.css';

// Types
interface LogEntry {
  id: string;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  node: string;
  message: string;
  traceId?: string;
}

interface TraceSpan {
  id: string;
  name: string;
  service: string;
  duration_ms: number;
  status: 'ok' | 'error';
  children: TraceSpan[];
}

interface Trace {
  id: string;
  timestamp: string;
  duration_ms: number;
  service: string;
  operation: string;
  status: 'ok' | 'error';
  spans: TraceSpan[];
}

interface MetricPoint {
  timestamp: string;
  value: number;
}

interface MetricSeries {
  name: string;
  unit: string;
  points: MetricPoint[];
  current: number;
  trend: 'up' | 'down' | 'stable';
}

type TabId = 'logs' | 'traces' | 'metrics';

// Mock data generators
function generateMockLogs(count: number): LogEntry[] {
  const levels: LogEntry['level'][] = ['debug', 'info', 'info', 'info', 'warn', 'error'];
  const nodes = ['edge-1', 'edge-2', 'core-1', 'core-2', 'compute-1'];
  const messages = [
    'Circuit execution complete',
    'Connection established',
    'Request processed successfully',
    'Witness selection started',
    'PoVC proof generated',
    'Lattice sync complete',
    'Rate limit exceeded',
    'Connection timeout',
    'Circuit timeout: aggregation',
    'Authentication failed',
  ];

  const logs: LogEntry[] = [];
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    const level = levels[Math.floor(Math.random() * levels.length)];
    logs.push({
      id: `log-${i}`,
      timestamp: new Date(now - i * 1000 * Math.random() * 60).toISOString(),
      level,
      node: nodes[Math.floor(Math.random() * nodes.length)],
      message: messages[Math.floor(Math.random() * messages.length)],
      traceId: Math.random() > 0.7 ? `trace-${Math.random().toString(36).substr(2, 8)}` : undefined,
    });
  }

  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

function generateMockTraces(count: number): Trace[] {
  const operations = ['circuit.execute', 'auth.verify', 'lattice.sync', 'witness.select', 'povc.generate'];
  const services = ['edge-proxy', 'lattice-router', 'compute-node', 'witness-pool'];

  const traces: Trace[] = [];
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    const duration = Math.floor(Math.random() * 200) + 10;
    const hasError = Math.random() > 0.9;

    traces.push({
      id: `trace-${Math.random().toString(36).substr(2, 8)}`,
      timestamp: new Date(now - i * 1000 * 60).toISOString(),
      duration_ms: duration,
      service: services[0],
      operation: operations[Math.floor(Math.random() * operations.length)],
      status: hasError ? 'error' : 'ok',
      spans: [
        {
          id: 'span-1',
          name: 'edge-proxy',
          service: 'edge-proxy',
          duration_ms: Math.floor(duration * 0.1),
          status: 'ok',
          children: [
            {
              id: 'span-2',
              name: 'auth validation',
              service: 'edge-proxy',
              duration_ms: Math.floor(duration * 0.05),
              status: 'ok',
              children: [],
            },
          ],
        },
        {
          id: 'span-3',
          name: 'lattice-router',
          service: 'lattice-router',
          duration_ms: Math.floor(duration * 0.15),
          status: 'ok',
          children: [
            {
              id: 'span-4',
              name: 'node selection',
              service: 'lattice-router',
              duration_ms: Math.floor(duration * 0.1),
              status: 'ok',
              children: [],
            },
          ],
        },
        {
          id: 'span-5',
          name: 'node-execution',
          service: 'compute-node',
          duration_ms: Math.floor(duration * 0.6),
          status: hasError ? 'error' : 'ok',
          children: [
            {
              id: 'span-6',
              name: 'circuit load',
              service: 'compute-node',
              duration_ms: Math.floor(duration * 0.1),
              status: 'ok',
              children: [],
            },
            {
              id: 'span-7',
              name: 'execute',
              service: 'compute-node',
              duration_ms: Math.floor(duration * 0.4),
              status: hasError ? 'error' : 'ok',
              children: [],
            },
            {
              id: 'span-8',
              name: 'proof generation',
              service: 'compute-node',
              duration_ms: Math.floor(duration * 0.1),
              status: 'ok',
              children: [],
            },
          ],
        },
      ],
    });
  }

  return traces;
}

function generateMockMetrics(): MetricSeries[] {
  const now = Date.now();
  const points = 60;

  const generatePoints = (baseValue: number, variance: number): MetricPoint[] => {
    const result: MetricPoint[] = [];
    for (let i = points - 1; i >= 0; i--) {
      result.push({
        timestamp: new Date(now - i * 60000).toISOString(),
        value: baseValue + (Math.random() - 0.5) * variance,
      });
    }
    return result;
  };

  return [
    {
      name: 'TPS',
      unit: 'tx/s',
      points: generatePoints(1250, 200),
      current: 1287,
      trend: 'up',
    },
    {
      name: 'Latency P99',
      unit: 'ms',
      points: generatePoints(45, 15),
      current: 42,
      trend: 'down',
    },
    {
      name: 'Error Rate',
      unit: '%',
      points: generatePoints(0.3, 0.2),
      current: 0.2,
      trend: 'stable',
    },
    {
      name: 'Circuit Executions',
      unit: '/min',
      points: generatePoints(850, 100),
      current: 892,
      trend: 'up',
    },
    {
      name: 'Queue Depth',
      unit: 'ops',
      points: generatePoints(25, 15),
      current: 18,
      trend: 'down',
    },
    {
      name: 'Node Health',
      unit: '%',
      points: generatePoints(98, 2),
      current: 100,
      trend: 'stable',
    },
  ];
}

// Components
function LogLevelBadge({ level }: { level: LogEntry['level'] }) {
  return <span className={`log-level log-level-${level}`}>{level.toUpperCase()}</span>;
}

function LogViewer({
  logs,
  onSearch,
  onLevelFilter,
  onTimeRangeChange,
}: {
  logs: LogEntry[];
  onSearch: (query: string) => void;
  onLevelFilter: (level: string) => void;
  onTimeRangeChange: (range: string) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('1h');

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    onSearch(e.target.value);
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLevelFilter(e.target.value);
    onLevelFilter(e.target.value);
  };

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTimeRange(e.target.value);
    onTimeRangeChange(e.target.value);
  };

  const formatTime = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', { hour12: false });
  };

  return (
    <div className="log-viewer">
      <div className="log-controls">
        <input
          type="text"
          placeholder="Search logs..."
          value={searchQuery}
          onChange={handleSearch}
          className="log-search"
        />
        <select value={levelFilter} onChange={handleLevelChange} className="log-filter">
          <option value="all">All Levels</option>
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>
        <select value={timeRange} onChange={handleTimeRangeChange} className="log-filter">
          <option value="15m">Last 15 min</option>
          <option value="1h">Last 1 hour</option>
          <option value="6h">Last 6 hours</option>
          <option value="24h">Last 24 hours</option>
        </select>
      </div>
      <div className="log-table-container">
        <table className="log-table">
          <thead>
            <tr>
              <th>Level</th>
              <th>Time</th>
              <th>Node</th>
              <th>Message</th>
              <th>Trace</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className={`log-row log-row-${log.level}`}>
                <td>
                  <LogLevelBadge level={log.level} />
                </td>
                <td className="log-time">{formatTime(log.timestamp)}</td>
                <td className="log-node">{log.node}</td>
                <td className="log-message">{log.message}</td>
                <td className="log-trace">
                  {log.traceId && <a href={`#trace/${log.traceId}`}>{log.traceId.slice(0, 8)}</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TraceViewer({ traces, selectedTrace, onSelectTrace }: { traces: Trace[]; selectedTrace: Trace | null; onSelectTrace: (trace: Trace) => void }) {
  const formatTime = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', { hour12: false });
  };

  const renderSpan = (span: TraceSpan, totalDuration: number, depth: number = 0) => {
    const widthPercent = (span.duration_ms / totalDuration) * 100;

    return (
      <div key={span.id} className="trace-span" style={{ marginLeft: `${depth * 20}px` }}>
        <div className="span-header">
          <span className="span-name">{span.name}</span>
          <span className="span-duration">{span.duration_ms}ms</span>
        </div>
        <div className="span-bar-container">
          <div
            className={`span-bar span-bar-${span.status}`}
            style={{ width: `${widthPercent}%` }}
          />
        </div>
        {span.children.map((child) => renderSpan(child, totalDuration, depth + 1))}
      </div>
    );
  };

  return (
    <div className="trace-viewer">
      <div className="trace-list">
        <h3>Recent Traces</h3>
        <div className="trace-list-items">
          {traces.map((trace) => (
            <div
              key={trace.id}
              className={`trace-list-item ${selectedTrace?.id === trace.id ? 'selected' : ''} ${trace.status}`}
              onClick={() => onSelectTrace(trace)}
            >
              <div className="trace-item-header">
                <span className="trace-operation">{trace.operation}</span>
                <span className={`trace-status trace-status-${trace.status}`}>
                  {trace.status === 'ok' ? '✓' : '✗'}
                </span>
              </div>
              <div className="trace-item-meta">
                <span className="trace-time">{formatTime(trace.timestamp)}</span>
                <span className="trace-duration">{trace.duration_ms}ms</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="trace-detail">
        {selectedTrace ? (
          <>
            <div className="trace-detail-header">
              <h3>Trace: {selectedTrace.id}</h3>
              <span className="trace-total-duration">Duration: {selectedTrace.duration_ms}ms</span>
            </div>
            <div className="trace-spans">
              {selectedTrace.spans.map((span) => renderSpan(span, selectedTrace.duration_ms))}
            </div>
          </>
        ) : (
          <div className="trace-empty">Select a trace to view details</div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ metric }: { metric: MetricSeries }) {
  const trendIcon = metric.trend === 'up' ? '↑' : metric.trend === 'down' ? '↓' : '→';
  const trendClass = metric.name.includes('Error') || metric.name.includes('Latency')
    ? metric.trend === 'down' ? 'good' : metric.trend === 'up' ? 'bad' : ''
    : metric.trend === 'up' ? 'good' : metric.trend === 'down' ? 'bad' : '';

  // Simple sparkline
  const points = metric.points.slice(-20);
  const max = Math.max(...points.map((p) => p.value));
  const min = Math.min(...points.map((p) => p.value));
  const range = max - min || 1;

  const sparklinePath = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * 100;
      const y = 100 - ((p.value - min) / range) * 100;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  return (
    <div className="metric-card">
      <div className="metric-header">
        <span className="metric-name">{metric.name}</span>
        <span className={`metric-trend ${trendClass}`}>{trendIcon}</span>
      </div>
      <div className="metric-value">
        {metric.current.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        <span className="metric-unit">{metric.unit}</span>
      </div>
      <svg className="metric-sparkline" viewBox="0 0 100 40" preserveAspectRatio="none">
        <path d={sparklinePath} fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    </div>
  );
}

function MetricsGrid({ metrics }: { metrics: MetricSeries[] }) {
  return (
    <div className="metrics-grid">
      {metrics.map((metric) => (
        <MetricCard key={metric.name} metric={metric} />
      ))}
    </div>
  );
}

// Main Component
export default function Observability() {
  const [activeTab, setActiveTab] = useState<TabId>('logs');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [traces, setTraces] = useState<Trace[]>([]);
  const [metrics, setMetrics] = useState<MetricSeries[]>([]);
  const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);

  // Load initial data
  useEffect(() => {
    setLogs(generateMockLogs(50));
    setTraces(generateMockTraces(20));
    setMetrics(generateMockMetrics());
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (activeTab === 'logs') {
        setLogs(generateMockLogs(50));
      } else if (activeTab === 'traces') {
        setTraces(generateMockTraces(20));
      } else if (activeTab === 'metrics') {
        setMetrics(generateMockMetrics());
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, activeTab]);

  const handleLogSearch = useCallback((query: string) => {
    console.log('Search logs:', query);
    // In production, filter logs or query API
  }, []);

  const handleLogLevelFilter = useCallback((level: string) => {
    console.log('Filter level:', level);
    // In production, filter logs
  }, []);

  const handleTimeRangeChange = useCallback((range: string) => {
    console.log('Time range:', range);
    // In production, query API with new range
  }, []);

  return (
    <div className="observability-page">
      <div className="page-header">
        <h1>Observability</h1>
        <div className="header-controls">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            disabled={!autoRefresh}
            className="refresh-interval"
          >
            <option value={5000}>5s</option>
            <option value={10000}>10s</option>
            <option value={30000}>30s</option>
            <option value={60000}>1m</option>
          </select>
        </div>
      </div>

      <div className="tab-bar">
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Logs
        </button>
        <button
          className={`tab ${activeTab === 'traces' ? 'active' : ''}`}
          onClick={() => setActiveTab('traces')}
        >
          Traces
        </button>
        <button
          className={`tab ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          Metrics
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'logs' && (
          <LogViewer
            logs={logs}
            onSearch={handleLogSearch}
            onLevelFilter={handleLogLevelFilter}
            onTimeRangeChange={handleTimeRangeChange}
          />
        )}
        {activeTab === 'traces' && (
          <TraceViewer
            traces={traces}
            selectedTrace={selectedTrace}
            onSelectTrace={setSelectedTrace}
          />
        )}
        {activeTab === 'metrics' && <MetricsGrid metrics={metrics} />}
      </div>
    </div>
  );
}
